import { useTeacherUnsavedChanges, useTeacherWorkspace } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import apiV2 from '../../../services/apiV2';

const fields = [
  ['observations', 'What happened? What evidence or classroom context explains it?'],
  ['reteach', 'Who needs a teacher check-in or another explanation? What will you check?'],
  ['practice', 'Who needs more practice or time with their current work?'],
  ['extension', 'Who may be ready for an extension? What evidence supports that?'],
  ['next_opening', 'How should the next bell ringer and shared concept change?'],
];
export function UnitDayReview() {
  const workspace = useTeacherWorkspace();
  const [params] = useSearchParams();
  const [units, setUnits] = useState<any[]>([]);
  const [selected, setSelected] = useState('');
  const [date, setDate] = useState('');
  const [review, setReview] = useState<any>(null);
  const [reflection, setReflection] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let active = true;
    apiV2.get('/unit-day-reviews/').then(({ data }) => {
      if (!active) return;
      const rows = (data.units || []).filter((row: any) => !workspace || String(row.section_id) === workspace.selected);
      setUnits(rows);
      if (rows.length) { setSelected(String(rows.find((row: any) => String(row.id) === params.get('unit'))?.id || rows[0].id)); setDate(params.get('date') || rows[0].today); }
    }).catch(() => { if (active) setError('Could not load your unit list. Reload to try again.'); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  useTeacherUnsavedChanges(dirty);
  const load = async (save = false) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const url = `/unit-day-reviews/${selected}/`;
      const options = { params: { date } };
      const { data } = save
        ? await apiV2.patch(url, { revision: review.revision, reflection }, options)
        : await apiV2.get(url, options);
      setReview(data);
      if (save || !dirty) setReflection(data.reflection || {});
      if (save) { setDirty(false); setNotice('Reflection saved. No lesson was changed or published.'); }
      else setNotice(dirty ? 'Evidence refreshed. Your unsaved notes are still here.' : 'Evidence refreshed.');
    } catch (e: any) { setError(e?.response?.data?.detail || 'Could not load or save the review. Your notes remain on screen.'); }
    finally { setBusy(false); }
  };
  return <AppShell role="teacher" title="Mixed-day unit review" navItems={teacherV2NavItems('prepare')}>
    <div className="space-y-5">
      <Link to="/v2/teacher/prepare" className="underline">Review &amp; Prepare</Link>
      <h2 className="text-2xl font-semibold">What do these students need next?</h2>
      <p>Review live checks, independent unit work and separate lab observations, then decide tomorrow’s shared opening and support groups.</p>
      {error && <p role="alert" className="text-danger">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-0 max-w-full flex-1">Unit<select aria-label="Unit" className="block w-full max-w-full rounded border p-2" value={selected} disabled={busy || dirty} onChange={e => {
          setSelected(e.target.value); setReview(null); setReflection({});
          setDate(units.find(u => String(u.id) === e.target.value)?.today || date);
        }}><option value="">Choose a unit</option>{units.map(u => <option key={u.id} value={u.id}>{u.section} — {u.title}</option>)}</select></label>
        <label>School date<input aria-label="School date" type="date" className="block rounded border p-2" value={date} disabled={busy || dirty} onChange={e => { setDate(e.target.value); setReview(null); setReflection({}); }} /></label>
        <button className="btn-primary" disabled={!selected || !date || busy} onClick={() => void load()}>Refresh evidence</button>
      </div>
      {dirty && <p>Save your notes before switching dates or units. Keep this page open until saved.</p>}
      {!units.length && !error && <p>No configured units are available for your classes yet.</p>}
      {review && <>
        <section className="rounded-lg border p-4 space-y-2">
          <h3 className="font-semibold">{review.section} · {review.title}</h3>
          <p>{review.date} · {review.snapshot.timezone}</p>
          <p>{review.snapshot.limitation}</p>
          <p>Saved responses: {review.snapshot.totals.saved_responses} · Finished activities: {review.snapshot.totals.finished_activities} · Live checks: {review.snapshot.totals.live_checks} · Lab records updated: {review.snapshot.totals.lab_records_updated || 0}</p>
        </section>
        <div className="overflow-x-auto"><table className="w-full text-left">
          <caption className="text-left font-semibold mb-2">Current progress and retained evidence from the selected day</caption>
          <thead><tr><th className="p-2">Student</th><th className="p-2">Independent work</th><th className="p-2">Live and lab evidence on this date</th></tr></thead>
          <tbody>{review.snapshot.students.map((student: any) => <tr key={student.student_id} className="border-t align-top">
            <th className="p-2">{student.name}</th><td className="p-2 space-y-2">{student.work.length ? student.work.map((work: any) => <div key={work.assignment_id}>
              <Link className="underline" to={work.href}>{work.title}</Link> · {work.status.replace('_', ' ')} · attempt {work.attempt}
              <p className="text-sm">{work.saved_today} responses saved on this date{work.finished_today ? ' · finished on this date' : ''}{work.needs_revision ? ' · revision requested' : ''}</p>
            </div>) : 'No released work'}</td><td className="p-2">{student.live_checks_today}
              {(student.labs_today || []).map((lab: any, index: number) => <div key={index} className="mt-3"><strong>Lab: {lab.title}</strong><p>{lab.participation} · verification: {lab.clearance}</p>{Object.entries(lab.performance || {}).map(([key,value]) => <p key={key}>{key}: {String(value).replace(/_/g,' ')}</p>)}</div>)}
              {student.live_runs.map((id: number) => <Link key={id} className="block underline" to={`/v2/teacher/run/${id}?student=${student.student_id}`}>Review live evidence</Link>)}
            </td></tr>)}</tbody>
        </table></div>
        <section className="rounded-lg border p-4 space-y-3"><h3 className="font-semibold">Your interpretation and next-day plan</h3>
          <p>Use submitted work, observations, goals, and check-ins together. Progress alone does not assign a student to a group.</p>
          {fields.map(([key, label]) => <label key={key} className="block">{label}<textarea className="mt-1 block w-full rounded border p-2" rows={3} maxLength={10000} value={reflection[key] || ''} disabled={busy} onChange={e => { setReflection({ ...reflection, [key]: e.target.value }); setDirty(true); }} /></label>)}
          <button className="btn-primary" disabled={busy || !dirty} onClick={() => void load(true)}>Save reflection</button>
        </section>
        <details className="rounded-lg border p-4"><summary>Planning brief without student identities</summary>
          <p className="my-2">Includes numeric totals only. Your private notes and student responses are excluded. Review any details you add before sharing.</p>
          <textarea aria-label="Planning brief" readOnly rows={12} className="w-full rounded border p-2" value={review.planning_brief} />
          <button className="btn-primary" onClick={() => {
            const url = URL.createObjectURL(new Blob([review.planning_brief], { type: 'text/plain;charset=utf-8' }));
            const link = document.createElement('a'); link.href = url; link.download = `unit-day-planning-${review.date}.txt`; link.click(); URL.revokeObjectURL(url);
          }}>Download planning brief</button>
        </details>
      </>}
    </div>
  </AppShell>;
}
